// See App.tsx's own comment on this same import -- required for the
// classic JSX transform this app's tsconfig uses.
import * as React from 'react';
import { useState } from 'react';
import { getStoredSession } from '@accenturecodefoundry/mfe-pot-shared-auth/core';
import type { ContentClient } from '@accenturecodefoundry/mfe-pot-shared-content-client';
import { fillTemplate } from '@accenturecodefoundry/mfe-pot-shared-content-client';
import type { Locale } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import type { EiApplicationInput, EiClaim, EmploymentInsuranceApiClient } from 'employment-insurance-data-access';
import { APPLICATIONS_CONTENT_KEYS } from './content-client';
import { EiApplicationForm } from './EiApplicationForm';
import { usePageContents } from './use-page-contents';

export interface ApplicationsProps {
  apiClient: EmploymentInsuranceApiClient;
  contentClient: ContentClient;
  locale: Locale;
  /**
   * Called once a submit succeeds, with the claim the BFF created (spec 015).
   * App uses it to switch the page into report mode without a reload; this
   * component still shows its own confirmation card as before.
   */
  onClaimCreated?: (claim: EiClaim) => void;
}

function confirmationTone(claim: EiClaim): 'success' | undefined {
  return claim.status === 'approved' ? 'success' : undefined;
}

export function Applications({ apiClient, contentClient, locale, onClaimCreated }: ApplicationsProps) {
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<EiClaim | null>(null);
  const [submitError, setSubmitError] = useState(false);
  const { content } = usePageContents(contentClient, APPLICATIONS_CONTENT_KEYS, locale);

  function label(key: (typeof APPLICATIONS_CONTENT_KEYS)[number]): string {
    return content[key]?.title ?? key;
  }

  function confirmationDescription(claim: EiClaim): string {
    return fillTemplate(label('employment-insurance.applications.confirmationDescription'), {
      status: claim.status,
      amount: claim.weeklyBenefitAmount.toFixed(2),
    });
  }

  async function apply(application: EiApplicationInput): Promise<void> {
    const session = getStoredSession();
    if (!session) {
      return;
    }
    setSubmitting(true);
    try {
      const claim = await apiClient.applyForEi(session.sub, application);
      setConfirmation(claim);
      onClaimCreated?.(claim);
    } catch (err) {
      console.error('Failed to submit EI application', err);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="ei-applications">
      {/* h2, not h1: the page-identity h1 is App.tsx's CMS intro title
          ("Employment Insurance"), or ReportingOverview's own heading when
          this section stays above it right after a submit. Two h1s on the composed /employment-insurance page is broken heading
          structure that only appears once this remote is mounted in the shell
          (the shell's AppFrame renders no h1 on this route), so per-MFE axe
          scans can't catch it. */}
      <h2>{label('employment-insurance.applications.heading')}</h2>
      {confirmation ? (
        <scds-card
          role="status"
          card-title={fillTemplate(label('employment-insurance.claims.cardTitle'), { id: confirmation.id })}
          description={confirmationDescription(confirmation)}
          tone={confirmationTone(confirmation)}
        />
      ) : submitError ? (
        <p role="alert">{label('employment-insurance.applications.error')}</p>
      ) : (
        <EiApplicationForm apiClient={apiClient} contentClient={contentClient} locale={locale} submitting={submitting} onSubmit={(application) => void apply(application)} />
      )}
    </section>
  );
}
